import { useNode } from "@craftjs/core";
import React, { useEffect } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import useApplyGlobalSettings, { CODEBLOCK_TYPES } from "../../../../hooks/useApplyGlobalSettings";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { toggleSegmentSettings } from "../../../../redux/actions/segmentSettings";
import { updateDividerStyles } from "../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";
import {
  isValidSuggestedColor,
  sliderToOpacity,
} from "../../../../utils/helpers";
import { DIVIDER_SVG_STYLES_V3_ENABLED } from "../../../../constants/featureFlags";
import {
  buildDividerSVG,
  isSvgDividerStyle,
  isAnimatable,
  isFlippable,
} from "../../Shared/DividerStyles/svgDividers";
import CustomSwitch from "../../../CustomSwitch";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import ColorPopup from "../../Shared/ColorPopup";
import DividerStyles from "../../Shared/DividerStyles";
import MarginBox from "../../Shared/MarginBox";
import SettingsSlider from "../../Shared/SettingsSlider";

// SVG-divider render wrapper (flag ON only). Carries the CSS-only drift keyframes
// used by the injected <g class="dv-drift"> (GPU transform only) and FREEZES all
// motion under prefers-reduced-motion. Flip mirrors asymmetric styles.
const SvgDividerWrap = styled.div`
  width: 100%;
  & svg {
    display: block;
    width: 100%;
    height: auto;
    max-width: 100%;
  }
  & .dv-drift {
    animation: dv-drift var(--dur, 8s) linear infinite;
    will-change: transform;
  }
  & .dv-drift.rev {
    animation-direction: reverse;
  }
  @keyframes dv-drift {
    to {
      transform: translateX(var(--dx, -48px));
    }
  }
  @media (prefers-reduced-motion: reduce) {
    & .dv-drift,
    & .dv-drift.rev {
      animation: none !important;
    }
  }
`;

// Advanced-control rows (flag ON only) — deck ewflow-picker-text advanced pane.
const CtlRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 38px;
  margin-top: 8px;
  font-family: "DM Sans", sans-serif;
  .lbl {
    font-size: 13px;
    font-weight: 600;
    color: #123051;
    display: block;
  }
  .sub {
    font-size: 10.5px;
    color: #9a999a;
    margin-top: 2px;
    display: block;
  }
`;

const DirSeg = styled.div`
  display: inline-flex;
  border: 1px solid #e1e1e1;
  border-radius: 8px;
  overflow: hidden;
  margin: 4px 0 2px;
  button {
    height: 32px;
    padding: 0 14px;
    background: #fff;
    border: none;
    font-family: "DM Sans", sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: #565556;
    cursor: pointer;
  }
  button.on {
    background: #8f38ef;
    color: #fff;
  }
  button + button {
    border-left: 1px solid #e1e1e1;
  }
`;

export const Divider = ({
  width,
  color,
  style,
  isApplyMarginOnAllSides,
  margin,
  allSideMargin,
  // Segments saved before the opacity control shipped have no opacity prop
  // on their Divider nodes; without a default, `1 - opacity` below renders
  // an invalid `NaN` opacity until the reducer-sync effect backfills it.
  opacity = 0,
  svgStyle,
  animate,
  animateDir,
  flip,
}) => {
  const {
    connectors: { connect, drag },
    actions: { setProp },
    id
  } = useNode();

  const { getPaletteColor } = usePaletteColor();
  const { divider: dividerProps } = useSelector((state) => state.codeblockStyles);

  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.Divider);

  // Init with divider props for new components
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setProp((props) => {
        // Only apply reducer values if component props are null or undefined
        // This ensures we don't override manually set values
        if (props.width === undefined) {
          props.width = dividerProps.width;
        }
        if (props.color === undefined) {
          props.color = dividerProps.color || "suggested_color_0";
        }
        if (props.style === undefined) {
          props.style = dividerProps.style;
        }
        if (props.margin === undefined) {
          props.margin = { ...dividerProps.margin };
        }
        if (props.isApplyMarginOnAllSides === undefined) {
          props.isApplyMarginOnAllSides = dividerProps.isApplyMarginOnAllSides;
        }
        if (props.allSideMargin === undefined) {
          props.allSideMargin = dividerProps.allSideMargin;
        }
        if (props.opacity === undefined) {
          props.opacity = dividerProps.opacity;
        }
        if (DIVIDER_SVG_STYLES_V3_ENABLED) {
          if (props.svgStyle === undefined) props.svgStyle = dividerProps.svgStyle;
          if (props.animate === undefined) props.animate = dividerProps.animate;
          if (props.animateDir === undefined) props.animateDir = dividerProps.animateDir;
          if (props.flip === undefined) props.flip = dividerProps.flip;
        }
      });
    }, 50);

    return () => clearTimeout(timeoutId);
  }, []);

  // Set default suggested color if none is provided
  useEffect(() => {
    if (!color) {
      const timeoutId = setTimeout(() => {
        setProp((props) => {
          props.color = "suggested_color_0";
        });
      }, 10);

      return () => clearTimeout(timeoutId);
    }
  }, [color, setProp]);

  const marginStyle = isApplyMarginOnAllSides
    ? `${allSideMargin}px`
    : margin
    ? `${margin?.top}px ${margin?.right}px ${margin?.bottom}px ${margin?.left}px`
    : undefined;

  const isGradient =
    color?.includes("linear-gradient") || color?.includes("radial-gradient");

  // DIVIDER_SVG_STYLES_V3: render an inline SVG only when the flag is ON AND this
  // divider has opted into an SVG style. Pre-existing dividers (svgStyle unset)
  // and every divider while the flag is OFF keep the legacy CSS border-top render
  // below, byte-identical.
  const useSvg = DIVIDER_SVG_STYLES_V3_ENABLED && isSvgDividerStyle(svgStyle);
  const resolvedColor = isValidSuggestedColor(color)
    ? getPaletteColor(color)
    : color;

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        <div
          style={{
            display: "flex",
            flex: 1,
            cursor: "pointer",
            padding: marginStyle,
          }}
        >
          {useSvg ? (
            <SvgDividerWrap
              style={{
                width: "100%",
                opacity: 1 - opacity,
                transform: flip ? "scaleX(-1)" : "none",
              }}
              dangerouslySetInnerHTML={{
                __html: buildDividerSVG(svgStyle, {
                  color: resolvedColor,
                  thickness: width,
                  animate: !!animate,
                  direction: animateDir === "right" ? "right" : "left",
                  uid: id,
                }),
              }}
            />
          ) : (
            <div
              style={{
                width: "100%",
                borderWidth: 0,
                borderTopWidth: width,
                borderStyle: style,
                borderTopColor: isGradient
                  ? "transparent"
                  : isValidSuggestedColor(color)
                  ? getPaletteColor(color)
                  : color,
                borderImage: isGradient ? `${color} 1` : "none",
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                opacity: 1 - opacity,
              }}
            />
          )}
        </div>
      </BlockIndicator>
    </div>
  );
};

const DividerSettings = () => {
  const {
    actions: { setProp },
    width,
    color,
    style,
    isApplyMarginOnAllSides,
    margin,
    allSideMargin,
    opacity,
    svgStyle,
    animate,
    animateDir,
    flip,
  } = useNode((node) => ({
    width: node.data.props.width,
    color: node.data.props.color,
    style: node.data.props.style,
    isApplyMarginOnAllSides: node.data.props.isApplyMarginOnAllSides,
    margin: node.data.props.margin,
    allSideMargin: node.data.props.allSideMargin,
    opacity: node.data.props.opacity,
    svgStyle: node.data.props.svgStyle,
    animate: node.data.props.animate,
    animateDir: node.data.props.animateDir,
    flip: node.data.props.flip,
  }));

  const { getPaletteColor } = usePaletteColor();
  const previewColor =
    (isValidSuggestedColor(color) ? getPaletteColor(color) : color) || "#123051";

  const handleAdjustment = (side, operation) => {
    const newValue =
      operation === "increase" ? margin[side] + 1 : margin[side] - 1;
    if (newValue >= 0) handleChange("margin", newValue, side);
  };
  const dispatch = useDispatch();

  const gradientRegex = /(linear|radial)-gradient\(([^)]+)\)/;

  const handleChange = (type, value, side) => {
    dispatch(toggleSegmentSettings(false));
    setProp((props) => {
      switch (type) {
        case "width":
          props.width = value;
          break;
        case "margin":
          if (value >= 0 && value <= 100) {
            props.margin[side] = value;
          }
          break;
        case "allSideMargin":
          props.allSideMargin = value;
          break;
        default:
        case "marginCheckbox":
          props.isApplyMarginOnAllSides = !props.isApplyMarginOnAllSides;
          break;
        case "style":
          props.style = value;
          break;
        case "color":
          props.color = value;
          if (gradientRegex.test(value)) props.style = "solid";
          break;
        case "resetColor":
          props.color = "#000000";
          break;
        case "opacity":
          props.opacity = sliderToOpacity(value);
          break;
        case "svgStyle":
          props.svgStyle = value;
          break;
        case "animate":
          props.animate = value;
          break;
        case "animateDir":
          props.animateDir = value;
          break;
        case "flip":
          props.flip = value;
          break;
      }

      // Create a new divider props object to update the reducer
      const updatedDividerProps = {
        width: props.width,
        color: props.color,
        style: props.style,
        margin: { ...props.margin },
        isApplyMarginOnAllSides: props.isApplyMarginOnAllSides,
        allSideMargin: props.allSideMargin,
        opacity: props.opacity,
        // New SVG-divider fields persist as last-used defaults ONLY when the flag
        // is on, so the OFF-path reducer payload is byte-identical to today.
        ...(DIVIDER_SVG_STYLES_V3_ENABLED
          ? {
              svgStyle: props.svgStyle,
              animate: props.animate,
              animateDir: props.animateDir,
              flip: props.flip,
            }
          : {}),
      };

      // Update the divider reducer
      dispatch(updateDividerStyles(updatedDividerProps));
    }, 500);
  };

  return (
    <>
      <DividerStyles
        selectedStyle={style}
        setSelectedStyle={(style) => handleChange("style", style)}
        svgStyle={svgStyle}
        setSvgStyle={(id) => handleChange("svgStyle", id)}
        previewColor={previewColor}
      />

      <SettingsSlider
        title="Thickness"
        sliderValue={width}
        onValueChange={(val) => handleChange("width", val)}
        min={1}
      />

      {DIVIDER_SVG_STYLES_V3_ENABLED && isAnimatable(svgStyle) && (
        <div>
          <CtlRow>
            <span>
              <span className="lbl">Animate this divider</span>
              <span className="sub">
                Gentle left/right drift &middot; CSS-only &middot; freezes under
                reduced-motion
              </span>
            </span>
            <CustomSwitch
              checked={!!animate}
              onChange={(e) => handleChange("animate", e.target.checked)}
              inputProps={{ "aria-label": "Animate this divider" }}
            />
          </CtlRow>
          {animate && (
            <DirSeg role="group" aria-label="Drift direction">
              <button
                type="button"
                className={animateDir !== "right" ? "on" : ""}
                aria-pressed={animateDir !== "right"}
                onClick={() => handleChange("animateDir", "left")}
              >
                {"◀"} Left
              </button>
              <button
                type="button"
                className={animateDir === "right" ? "on" : ""}
                aria-pressed={animateDir === "right"}
                onClick={() => handleChange("animateDir", "right")}
              >
                Right {"▶"}
              </button>
            </DirSeg>
          )}
        </div>
      )}

      {DIVIDER_SVG_STYLES_V3_ENABLED && isFlippable(svgStyle) && (
        <CtlRow>
          <span>
            <span className="lbl">Flip horizontally</span>
            <span className="sub">
              Mirrors asymmetric styles (waves, chevron, wheat&hellip;)
            </span>
          </span>
          <CustomSwitch
            checked={!!flip}
            onChange={(e) => handleChange("flip", e.target.checked)}
            inputProps={{ "aria-label": "Flip horizontally" }}
          />
        </CtlRow>
      )}
      <MarginBox
        margin={margin}
        increaseMargin={(side) => handleAdjustment(side, "increase")}
        decreaseMargin={(side) => handleAdjustment(side, "decrease")}
        isApplyMarginOnAllSides={isApplyMarginOnAllSides}
        allSideMargin={allSideMargin}
        handleMarginCheckBox={() => handleChange("marginCheckbox")}
        handleSliderMarginChange={(value) =>
          handleChange("allSideMargin", value)
        }
        handleMarginChange={(side, newValue) =>
          handleChange("margin", newValue, side)
        }
      />
      <ColorPopup
        title="Color"
        background={color}
        handleChangeBackgroundColor={(val) => handleChange("color", val)}
        resetBackgroundColor={() => handleChange("resetColor")}
        sliderValue={opacity * 100}
        sliderChange={(val) => handleChange("opacity", val)}
        sliderBoxTitle={"Transparency"}
        showSuggestedColorPalette={true}
        isHideGradient={true}
      />
    </>
  );
};

export const DividerDefaultProps = {
  width: undefined, // Will get from reducer
  color: undefined, // Color will be set based on selectedSegmentColor or reducer
  style: undefined, // Will get from reducer
  margin: undefined, // Will get from reducer
  isApplyMarginOnAllSides: undefined, // Will get from reducer
  allSideMargin: undefined, // Will get from reducer
  opacity: undefined, // Will get from reducer
  // DIVIDER_SVG_STYLES_V3 (undefined => legacy CSS render; only read when flag ON)
  svgStyle: undefined,
  animate: undefined,
  animateDir: undefined,
  flip: undefined,
};

Divider.craft = {
  props: DividerDefaultProps,
  related: {
    settings: DividerSettings,
  },
  displayName: 'DividerWidget',
};
